import React, { useState } from 'react';
import { Link } from 'react-router';
import { accessLevelsApi } from '../../requests/accessLevels';

const LABELS = {
    no: 'No', yes: 'Yes', none: 'None', view: 'View', edit: 'Edit', own: 'Own', all: 'All',
    limited: 'Limited', edit_own: 'Edit own', edit_all: 'Edit all',
    loans: 'Loans only', except_continuum: 'All but Brokerage Continuum',
    dashboard: 'Stats Dashboard', peak_stats: 'Peak Stats',
};

/** Members in the order the server sends them: by last name, then first name. */
function MemberList({ members }) {
    return (
        <ul className="al-members">
            {members.map((m) => (
                <li key={m.id}>
                    <Link to={arc.path.base + 'teamMember/' + m.id}>{m.name || m.email}</Link>
                    {!m.active && <span className="al-members__inactive"> (inactive)</span>}
                </li>
            ))}
        </ul>
    );
}

/**
 * Saving sends only the permissions that changed; the server merges them and
 * refuses a change that would leave nobody able to manage access levels.
 * `readOnly` (anyone but the Founder) shows the level without a way to change it.
 */
export default function AccessLevelEditor({ level, catalog, onSaved, onDelete, readOnly = false }) {
    const [name, setName] = useState(level.name);
    const [description, setDescription] = useState(level.description || '');
    const [values, setValues] = useState(level.permissions);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const changed = Object.keys(values).filter((key) => values[key] !== level.permissions[key]);
    const dirty = changed.length > 0 || name !== level.name || description !== (level.description || '');
    // Active members first, inactive ones listed after them.
    const activeMembers = level.members.filter((m) => m.active);
    const inactiveMembers = level.members.filter((m) => !m.active);

    const save = () => {
        const permissions = {};
        changed.forEach((key) => { permissions[key] = values[key]; });
        setSaving(true);
        setMessage(null);
        accessLevelsApi.update(level.id, { name: name.trim(), description, permissions })
            .then((updated) => {
                onSaved(updated);
                setMessage({ type: 'success', text: 'Saved. Members see the change the next time they load iCLIMB.' });
            })
            .catch((e) => setMessage({ type: 'error', text: e.message }))
            .then(() => setSaving(false));
    };

    return (
        <div className="al-editor">
            {readOnly && (
                <p className="al-muted">Only the Founder can change what a level can do. You can choose members' levels on their Account Settings tab.</p>
            )}
            <div className="al-card al-head">
                <div className="al-head__fields">
                    <input className="al-input al-input--name" value={name} disabled={readOnly} onChange={(e) => setName(e.target.value)}/>
                    <textarea className="al-input al-input--text" value={description} disabled={readOnly}
                              placeholder={readOnly ? '' : 'What this level is for'} onChange={(e) => setDescription(e.target.value)}/>
                    {message && <div className={'al-notice al-notice--' + (message.type === 'success' ? 'success' : 'error')}>{message.text}</div>}
                </div>
                {!readOnly && <div className="al-head__actions">
                    <button type="button" className="al-btn al-btn--primary" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
                    {!level.is_protected && (
                        <button type="button" className="al-btn al-btn--danger" disabled={level.members.length > 0} onClick={onDelete}
                                title={level.members.length > 0 ? 'Give its members another level first.' : ''}>
                            Delete
                        </button>
                    )}
                </div>}
            </div>

            {catalog.map((group) => (
                <section key={group.name} className="al-card al-group">
                    <h2 className="al-group__title">{group.name}</h2>
                    {group.permissions.map((p) => {
                        const locked = readOnly || (level.is_protected && p.key === 'access.manage');
                        return (
                            <div key={p.key} className="al-row">
                                <div className="al-row__label">
                                    <div className="al-row__name">{p.label}</div>
                                    {p.help && <div className="al-row__help">{p.help}</div>}
                                    {locked && !readOnly && <div className="al-row__note">Always on for the Founder level</div>}
                                </div>
                                <div className={'al-seg' + (locked ? ' al-seg--locked' : '')} role="radiogroup" aria-label={p.label}>
                                    {p.values.map((v) => (
                                        <button key={v} type="button" role="radio" aria-checked={values[p.key] === v}
                                                disabled={locked}
                                                className={'al-seg__opt' + (values[p.key] === v ? ' al-seg__opt--on' : '')}
                                                onClick={() => setValues({ ...values, [p.key]: v })}>
                                            {LABELS[v] || v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </section>
            ))}

            <section className="al-card al-group">
                <h2 className="al-group__title">Members ({activeMembers.length})</h2>
                {activeMembers.length === 0 && <p className="al-muted">{level.members.length === 0 ? 'Nobody has this level.' : 'No active members have this level.'}</p>}
                <MemberList members={activeMembers}/>
                {inactiveMembers.length > 0 && (
                    <React.Fragment>
                        <h2 className="al-group__title al-group__title--sub">Inactive Members ({inactiveMembers.length})</h2>
                        <MemberList members={inactiveMembers}/>
                    </React.Fragment>
                )}
                <p className="al-muted">Change a member's level on their Account Settings tab.</p>
            </section>
        </div>
    );
}
